import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiMail, 
  FiMapPin, 
  FiLinkedin, 
  FiGithub, 
  FiYoutube,
  FiShield
} = FiIcons;

const Contact = ({ viewMode }) => {
  const contactInfo = [
    {
      icon: FiMail,
      label: "Email",
      value: "gary@techguy.fun",
      link: "mailto:gary@techguy.fun",
      description: "Best for detailed inquiries"
    },
    {
      icon: FiMapPin,
      label: "Location",
      value: "Magnolia, Texas",
      link: null,
      description: "Based in the Houston area"
    },
    {
      icon: FiLinkedin,
      label: "LinkedIn",
      value: "garyfagerholm",
      link: "https://www.linkedin.com/in/garyfagerholm/",
      description: "Professional networking"
    }
  ];

  const socialLinks = [
    { 
      icon: FiLinkedin, 
      label: "LinkedIn", 
      url: "https://www.linkedin.com/in/garyfagerholm/" 
    },
    { 
      icon: FiGithub, 
      label: "GitHub", 
      url: "https://github.com/garyfagerholm" 
    },
    { 
      icon: FiYoutube, 
      label: "YouTube", 
      url: "https://www.youtube.com/@techguyfun" 
    }
  ];

  const availability = [
    { type: "Full-time Opportunities", status: "Dance Card Full", color: "red" },
    { type: "Consulting Projects", status: "Available", color: "blue" },
    { type: "Speaking Engagements", status: "Accepting", color: "purple" },
    { type: "Mentorship", status: "Limited Slots", color: "orange" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Header */}
      <section className="section-padding bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">Let's Connect</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {viewMode === 'professional' ? 
              "Interested in security strategy or cloud infrastructure? Reach out and let's start a conversation." : 
              "Whether it's about BJJ, AI automation, or tech talk, I'm always happy to hear from you."}
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Embedded Form Column */}
            <motion.div 
              className="lg:col-span-2"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={itemVariants} className="h-full">
                <h2 className="font-heading text-3xl font-bold mb-8 text-gray-900">Send a Message</h2>
                <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-inner border border-gray-100 min-h-[700px]">
                  <iframe
                    src="https://formrobin.com/f/j2kn25j"
                    width="100%"
                    height="800px"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    title="Contact Form"
                    className="w-full"
                  >
                    Loading…
                  </iframe>
                </div>
              </motion.div>
            </motion.div>

            {/* Sidebar Columns */}
            <motion.div 
              className="space-y-8"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={itemVariants}>
                <h3 className="font-heading text-2xl font-bold mb-6 text-gray-900">Contact Details</h3>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={info.icon} className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{info.label}</h4>
                        {info.link ? (
                          <a href={info.link} className="text-teal-600 hover:text-teal-700 font-medium break-all">
                            {info.value}
                          </a>
                        ) : (
                          <span className="text-gray-700 font-medium">{info.value}</span>
                        )}
                        <p className="text-sm text-gray-600">{info.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h3 className="font-heading text-2xl font-bold mb-6 text-gray-900">Current Availability</h3>
                <div className="space-y-3">
                  {availability.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 font-medium">{item.type}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                        item.color === 'red' ? 'bg-red-100 text-red-800' : 
                        item.color === 'blue' ? 'bg-blue-100 text-blue-800' : 
                        item.color === 'purple' ? 'bg-purple-100 text-purple-800' : 
                        'bg-orange-100 text-orange-800'}`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h3 className="font-heading text-2xl font-bold mb-6 text-gray-900">Social Connections</h3>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a 
                      key={index} 
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-12 h-12 bg-gray-100 hover:bg-teal-100 rounded-lg flex items-center justify-center transition-all duration-300" 
                      whileHover={{ scale: 1.1, y: -2 }} 
                      whileTap={{ scale: 0.95 }}
                      aria-label={social.label}
                    >
                      <SafeIcon icon={social.icon} className="w-6 h-6 text-gray-600 hover:text-teal-600" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer Quote */}
      <section className="section-padding bg-gray-900 text-white">
        <motion.div 
          className="container mx-auto text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <SafeIcon icon={FiShield} className="w-12 h-12 mx-auto mb-6 text-teal-400" />
          <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4 italic">
            "Security is a journey, not a destination."
          </h2>
          <p className="text-gray-400">Looking forward to being part of yours.</p>
        </motion.div>
      </section>
    </div>
  );
};

export default Contact;